import ArticleLayout from './components/ArticleLayout';
import { articlesMeta } from '@/mocks/articlesData';

const data = articlesMeta.find((a) => a.slug === 'sugargoo-shipping-guide')!;

export default function ShippingGuide() {
  return (
    <ArticleLayout data={data}>
      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Introduction: Understanding Sugargoo Shipping Costs
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          One of the most important — and often most confusing — aspects of using a shopping agent is understanding <strong>Sugargoo shipping costs</strong>. Unlike domestic online shopping where shipping is usually a flat rate or free over a threshold, international agent shipping involves multiple variables that can significantly impact your total order cost.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          This complete guide breaks down every factor that affects Sugargoo shipping prices, compares all available methods, and shares proven strategies to minimize your shipping expenses without sacrificing reliability.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Whether you are shipping a single item or consolidating a full haul, understanding these cost dynamics will help you make smarter decisions and avoid unexpected charges.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          What Determines Your Sugargoo Shipping Cost?
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Sugargoo shipping costs are calculated based on four primary factors. Understanding each one helps you predict and control your expenses:
        </p>
        <div className="space-y-4 mb-4">
          {[
            {
              title: 'Package Weight',
              desc: 'The single biggest factor in shipping cost. Agents charge by either actual weight or volumetric weight — whichever is higher. Volumetric weight considers package dimensions, so a large but light box (like shoes with stuffing) can cost more than a small heavy box.',
            },
            {
              title: 'Destination Country',
              desc: 'Shipping to the USA, UK, Canada, Australia, or EU countries all have different base rates. Some shipping lines only serve specific regions, while others have broader coverage. Customs regulations and import duties also vary by country.',
            },
            {
              title: 'Shipping Method',
              desc: 'Express services like DHL and FedEx cost significantly more than economy options like EUB or SAL. The trade-off is speed and tracking reliability. Premium methods also tend to have better customs handling and insurance options.',
            },
            {
              title: 'Product Type & Value',
              desc: 'High-value items may require additional insurance or declaration handling. Restricted categories (batteries, liquids, branded goods) may limit which shipping methods are available or add surcharges.',
            },
          ].map((factor) => (
            <div key={factor.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-truck-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{factor.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{factor.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Complete Sugargoo Shipping Methods Comparison
        </h2>
        <div className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-200/60 bg-background-100/50">
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Method</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Timeline</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Cost Level</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Tracking</th>
                <th className="text-left px-4 py-3 font-semibold text-foreground-800">Best For</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['DHL', '3-7 days', 'High', 'Full', 'Urgent, high-value orders'],
                ['FedEx', '3-7 days', 'High', 'Full', 'Business, time-sensitive'],
                ['EMS', '7-15 days', 'Moderate', 'Full', 'Balanced speed and cost'],
                ['EUB / ePacket', '10-25 days', 'Lower', 'Limited', 'Small, light packages'],
                ['SAL', '15-30 days', 'Lowest', 'Basic', 'Maximum savings, no rush'],
                ['Sea Freight', '30-60 days', 'Bulk rate', 'Basic', 'Large, heavy hauls'],
                ['SF Express', '5-12 days', 'Moderate-High', 'Full', 'Asia-Pacific destinations'],
                ['UPS', '3-10 days', 'High', 'Full', 'North America priority'],
              ].map((row) => (
                <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                  <td className="px-4 py-2.5 font-medium text-foreground-800">{row[0]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[1]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[2]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[3]}</td>
                  <td className="px-4 py-2.5 text-foreground-600">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Note that availability varies by destination country. Some methods may be suspended or restricted based on current international shipping conditions and customs regulations.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          How to Calculate Volumetric Weight
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Volumetric weight is calculated using the formula: <strong>(Length × Width × Height) ÷ Divisor</strong>. The divisor varies by shipping method but is typically around 5000 for most couriers. If the volumetric weight exceeds the actual weight, you will be charged for the volumetric weight.
        </p>
        <div className="bg-background-100/60 rounded-xl p-4 md:p-5 border border-background-200/60 mb-4">
          <h4 className="text-sm font-semibold text-foreground-800 mb-2">Example Calculation</h4>
          <p className="text-sm text-foreground-600 leading-relaxed">
            A shoe box measures 35cm × 25cm × 15cm and weighs 1.2kg. The volumetric weight is (35 × 25 × 15) ÷ 5000 = 2.625kg. Since 2.625kg &gt; 1.2kg, you are charged for 2.625kg. Removing the shoe box and using a soft bag could reduce this significantly.
          </p>
        </div>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          This is why many experienced shoppers request <strong>package removal</strong> — having Sugargoo remove outer packaging, shoe boxes, and excess materials to reduce volumetric weight and save on shipping.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          7 Proven Strategies to Reduce Sugargoo Shipping Costs
        </h2>
        <div className="space-y-3">
          {[
            { title: 'Consolidate Multiple Items', desc: 'Ship multiple purchases in one package. The cost per item drops dramatically when you spread the base shipping charge across several products.' },
            { title: 'Remove Outer Packaging', desc: 'Request box removal, tag removal, and minimal packaging. This reduces volumetric weight and can save 20-40% on shipping for fashion items.' },
            { title: 'Choose Economy for Non-Urgent Orders', desc: 'If you do not need items quickly, SAL or EUB can cost 50-70% less than DHL. Plan ahead and use slower methods for non-essential purchases.' },
            { title: 'Avoid Shipping Restricted Items Together', desc: 'Some items (batteries, liquids, branded goods) require specific shipping lines. Mixing restricted and non-restricted items can force you onto more expensive shipping methods.' },
            { title: 'Pre-Plan Your Haul Size', desc: 'Small packages often have disproportionately high per-kg costs. Aim for 3-5kg consolidated packages for the best cost-to-weight ratio.' },
            { title: 'Check for Shipping Promotions', desc: 'Sugargoo occasionally runs shipping fee discounts or coupon campaigns. Timing your shipment around these promotions can lead to meaningful savings.' },
            { title: 'Declare Value Strategically', desc: 'Work within legal limits to declare accurate but reasonable values. Over-declaring can trigger unnecessary customs duties; under-declaring risks seizure.' },
          ].map((tip, i) => (
            <div key={tip.title} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 text-xs font-bold shrink-0">
                {i + 1}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-0.5">{tip.title}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{tip.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Country-Specific Shipping Notes
        </h2>
        <div className="space-y-4">
          {[
            { country: 'USA', note: 'EMS, DHL, and FedEx are reliable. EUB works well for small packages under 2kg. Customs is generally straightforward for personal goods under $800.' },
            { country: 'UK', note: 'EMS and DHL are popular. Be aware of VAT charges on imports over £135. Royal Mail handling fees may apply. SAL is sometimes suspended.' },
            { country: 'Canada', note: 'EMS and Canada Post partnerships work well. Expect potential duties on orders over CAD $20. DHL is fast but brokerage fees can add up.' },
            { country: 'Australia', note: 'EMS and DHL are reliable options. GST applies to most imports. Sea freight is viable for very large orders due to Australia distance.' },
            { country: 'Germany / EU', note: 'DHL and EMS are commonly used. Strict customs regulations for branded goods. VAT applies to all imports. EORI number may be required for larger shipments.' },
          ].map((item) => (
            <div key={item.country} className="flex gap-3 p-4 bg-background-100/60 rounded-xl border border-background-200/60">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.country}</h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.note}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Customs, Duties, and Import Taxes
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          <strong>Important:</strong> Sugargoo shipping quotes do not include customs duties or import taxes charged by your country. These are separate fees collected by your local customs authority or delivery carrier.
        </p>
        <ul className="space-y-2 mb-4">
          {[
            'USA: No duty on personal shipments under $800 (de minimis threshold)',
            'UK: VAT applies at 20% on most imports; handling fees from carriers',
            'Canada: Duties vary by product category; GST/HST applies',
            'Australia: 10% GST on most imports; duties depend on product type',
            'EU: VAT applies at local rate; customs duties vary by product HS code',
          ].map((item) => (
            <li key={item} className="flex items-start gap-3">
              <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
              <span className="text-sm text-foreground-700">{item}</span>
            </li>
          ))}
        </ul>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Always research your country specific import rules before placing large orders. The savings from shopping internationally can still be substantial even after accounting for duties, but unexpected charges are never pleasant.
        </p>
      </section>

      <section className="mb-10">
        <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-4">
          Conclusion: Master Your Sugargoo Shipping Strategy
        </h2>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          Understanding <strong>Sugargoo shipping costs</strong> is not just about finding the cheapest option — it is about finding the right balance of speed, cost, and reliability for your specific needs. A well-planned shipping strategy can reduce your total costs by 30-50% compared to shipping items individually with premium methods.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
          The key takeaways are simple: consolidate when possible, remove excess packaging, choose economy methods for non-urgent orders, and always factor in potential customs charges when budgeting. Combine these strategies with a good <strong>Sugargoo Spreadsheet</strong> for product discovery, and you have a powerful system for efficient international shopping.
        </p>
        <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
          Start small, learn the process with a test order, and scale your strategy as you gain experience. The savings and product access are well worth the initial learning curve.
        </p>
      </section>
    </ArticleLayout>
  );
}